/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import '@neo4j-ndl/base/lib/neo4j-ds-styles.css';

import { SideNavigation, Typography } from '@neo4j-ndl/react';
import {
  CodeBracketIconOutline,
  HomeIconOutline,
  MagnifyingGlassIconOutline,
  ServerIconOutline,
  WrenchScrewdriverIconOutline,
} from '@neo4j-ndl/react/icons';
import { useState } from 'react';

const Component = () => {
  const [active, setActive] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="n-h-[calc(40vh-32px)] n-min-h-[700px] n-flex n-bg-neutral-bg-default">
      <SideNavigation
        ariaLabel="Main side navigation"
        isExpanded={isExpanded}
        shouldOverlayOnInteraction={true}
        onPinButtonClick={() => setIsExpanded(!isExpanded)}
      >
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<HomeIconOutline />}
            label="Info Badge"
            isActive={active === 'nav-item-1'}
            onClick={() => setActive('nav-item-1')}
            badge={{
              number: 1,
              type: 'info',
            }}
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<CodeBracketIconOutline />}
            label="Warning Badge"
            isActive={active === 'nav-item-2'}
            onClick={() => setActive('nav-item-2')}
            badge={{
              number: 1,
              type: 'warning',
            }}
          />
        </SideNavigation.ListItem>
        <SideNavigation.ListItem>
          <SideNavigation.NavItem
            icon={<ServerIconOutline />}
            label="Critical Badge"
            isActive={active === 'nav-item-3'}
            onClick={() => setActive('nav-item-3')}
            badge={{
              number: 1,
              type: 'danger',
            }}
          />
        </SideNavigation.ListItem>
        <SideNavigation.Divider />
        <SideNavigation.ListItem>
          <SideNavigation.CategoryItem
            icon={<WrenchScrewdriverIconOutline />}
            label="Category Item"
            isActive={['nav-item-4', 'nav-item-5', 'nav-item-6'].includes(
              active ?? '',
            )}
            badge={{
              number: 3,
              type: 'danger',
            }}
          >
            <SideNavigation.ListItem>
              <SideNavigation.NavItem
                icon={<MagnifyingGlassIconOutline />}
                label="Info Badge"
                isActive={active === 'nav-item-4'}
                onClick={() => setActive('nav-item-4')}
                badge={{
                  number: 1,
                  type: 'info',
                }}
              />
            </SideNavigation.ListItem>
            <SideNavigation.ListItem>
              <SideNavigation.NavItem
                icon={<MagnifyingGlassIconOutline />}
                label="Warning Badge"
                isActive={active === 'nav-item-5'}
                onClick={() => setActive('nav-item-5')}
                badge={{
                  number: 1,
                  type: 'warning',
                }}
              />
            </SideNavigation.ListItem>
            <SideNavigation.ListItem>
              <SideNavigation.NavItem
                icon={<MagnifyingGlassIconOutline />}
                label="Critical Badge"
                isActive={active === 'nav-item-6'}
                onClick={() => setActive('nav-item-6')}
                badge={{
                  number: 1,
                  type: 'danger',
                }}
              />
            </SideNavigation.ListItem>
          </SideNavigation.CategoryItem>
        </SideNavigation.ListItem>
      </SideNavigation>
      <div className="n-h-full n-w-full">
        <div className="n-flex n-flex-col n-p-8 n-w-full n-max-w-[1024px] n-mx-auto">
          <div className="n-flex n-flex-col n-gap-token-8 n-bg-neutral-bg-weak n-p-token-32 n-rounded-lg n-items-center n-justify-center">
            <Typography variant="title-4">Side Nav with Badges</Typography>
            <Typography variant="body-medium">
              Badges can be used to indicate that there are actions that need
              attention from the user on the specific page. The badge can have
              three different types of severity:
              <b className="n-text-primary-text"> info</b>,
              <b className="n-text-warning-text"> warning</b> and
              <b className="n-text-danger-text"> danger</b>.
              <br />
              <br />
              The badge on a <b>CategoryItem</b> should be the type of the
              highest priority badge of the items in the category. The number
              should be the total number of badges in the category.
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
